import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Loader } from "@/components/Loader";

export default function AdminSettings() {
  const { t } = useApp();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loader />;

  const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success(t("saved")); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const uploadQr = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/settings/qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((x) => ({ ...x, sumup_qr_path: data.sumup_qr_path }));
      toast.success("QR uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const inputCls = "cu-input w-full px-3 py-2.5 text-sm";
  const F = ({ l, k, ph }) => (
    <div><label className="eyebrow block mb-1.5">{l}</label><input data-testid={`settings-${k}`} value={s[k] || ""} onChange={set(k)} placeholder={ph} className={inputCls} /></div>
  );

  return (
    <div className="fade-up pb-10">
      <h1 className="font-serif-cu text-3xl mb-8">{t("settings")}</h1>

      <Card title="SumUp & Payment">
        <div><label className="eyebrow block mb-1.5">SumUp Payment Link</label><input data-testid="settings-sumup_link" value={s.sumup_link || ""} onChange={set("sumup_link")} placeholder="https://pay.sumup.com/..." className={inputCls} /></div>
        <div>
          <label className="eyebrow block mb-1.5">{t("scan_to_pay")} (SumUp QR)</label>
          <div className="flex items-center gap-4">
            {s.sumup_qr_path && <div className="bg-white rounded-lg p-1.5"><img src={fileUrl(s.sumup_qr_path)} alt="QR" className="w-20 h-20 object-contain" /></div>}
            <label className="cu-input px-4 py-2.5 text-sm flex items-center gap-2 cursor-pointer flex-1" data-testid="settings-qr-upload">
              <Upload size={15} className="text-[color:var(--cu-gold)]" /><span className="text-[color:var(--cu-muted)]">{uploading ? "…" : s.sumup_qr_path ? "Change QR" : "Upload QR"}</span>
              {s.sumup_qr_path && <Check size={15} className="ml-auto text-emerald-400" />}
              <input type="file" accept="image/*" className="hidden" onChange={uploadQr} />
            </label>
          </div>
        </div>
      </Card>

      <Card title={t("bank_transfer")}>
        {F({ l: t("account_name"), k: "bank_account_name" })}
        {F({ l: t("iban"), k: "bank_iban" })}
        {F({ l: t("bic"), k: "bank_bic" })}
        <div><label className="eyebrow block mb-1.5">Note</label><input value={s.bank_note || ""} onChange={set("bank_note")} className={inputCls} /></div>
      </Card>

      <Card title={t("corporate_info")}>
        {F({ l: t("registered_company"), k: "company_name" })}
        {F({ l: t("company_no"), k: "companies_house_number" })}
        {F({ l: t("registered_office"), k: "registered_office" })}
        {F({ l: t("official_email"), k: "official_email" })}
        {F({ l: t("website"), k: "website" })}
        {F({ l: "WhatsApp Number", k: "whatsapp_number", ph: "+39..." })}
      </Card>

      <Card title="Legal">
        <Area l={t("membership_terms")} k="terms_membership" s={s} set={set} />
        <Area l={t("terms_conditions")} k="terms_conditions" s={s} set={set} />
        <Area l={t("privacy_policy")} k="privacy_policy" s={s} set={set} />
        <Area l={t("cookie_info")} k="cookie_info" s={s} set={set} />
      </Card>

      <button onClick={save} disabled={saving} data-testid="settings-save-button" className="btn-gold w-full py-3.5 text-sm sticky bottom-4">{t("save_changes")}</button>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="cu-card p-5 mb-6">
      <h3 className="eyebrow mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Area({ l, k, s, set }) {
  return <div><label className="eyebrow block mb-1.5">{l}</label><textarea data-testid={`settings-${k}`} value={s[k] || ""} onChange={set(k)} rows={3} className="cu-input w-full px-3 py-2.5 text-sm resize-none" /></div>;
}
